import LZString from "lz-string";

import { loginAPI, resendOTP, sendOTP, signUpAPI, UidSignUpAPI, validateBinNumber } from "../../../Services/APIs/Login";
import { passkeySignInAPI, passkeySignInInitAPI } from "../../../Services/APIs/Passkey";

import { cardTitleToPath } from "../../../Utils/Constant";
import { DETAIL_PAGE, LISTING_PAGE, SEARCH_PAGE } from "../../../Utils/Constants/Hotel";
import { CARD_NUMBER, LOGIN, SELECTEDCARD, SIGNUP, SUBID, UID, USERDATA } from "../../../Utils/Constants/Login";
import { base64UrlToArrayBuffer, bufferToBase64Url, extractHostname } from "../../../Utils/DataConvertUtils";
import { validateLoginForm, validateSignUpForm } from "../../../Utils/DataValidationUtils";
import { clearLocalStorage, removeLocalStorageItem, setLocalStorageItem } from '../../../Utils/StorageUtils';
import { getPreferredCardSelection } from "../../../Utils/CommonFunctions";
import { trackAuthSuccess } from "../../../Utils/Analytics";

import {
  COMMERCE_MODULE, HOMEPAGE, REWARD_LISTING, SUBSCRIPTION_LISTING, TRAVEL_FLIGHT_MODULE, TRAVEL_HOTEL_MODULE
} from "../../../Routes/RoutesContant";

export const getEditNameStatus = (data) => {
  const userData = data;

  // Logic remains the same
  const canEditUserName = !userData?.name || userData.name === '' || userData.name === undefined;
  
  // return object structure
  return { ...userData, canEditUserName };
};

export const switchToLogin = ({ setAuthMode, setShowLogin, setShowSignUp, setResendTimer, isAuthPageMode }) => {
  setAuthMode(LOGIN);
  if (!isAuthPageMode) {
    setShowLogin(true);
    setShowSignUp(false);
  }
  setResendTimer(0);
};

export const switchToSignUp = ({ setAuthMode, setShowLogin, setShowSignUp, setResendTimer, isAuthPageMode }) => {
  setAuthMode(SIGNUP);
  if (!isAuthPageMode) {
    setShowLogin(false);
    setShowSignUp(true);
  }
  setResendTimer(0);
};

export const closeAuthForm = ({ setShowLoginForm, setShowLogin, setShowSignUp, isAuthPageMode }) => {
  setShowLoginForm(false);
  if (!isAuthPageMode) {
    setShowLogin(false);
    setShowSignUp(false);
  }
};

export const handleBinInputChange = async ({ cardNumber, setBinMessage, setBinError, setRequireEmail, setOtpValidateInput }) => {
  if (!cardNumber || cardNumber.length !== 8) {
    setBinError("Please enter a valid 8-digit card number");
    return;
  }
  try {
    const response = await validateBinNumber(cardNumber);
    if (response.status === 200) {
      setBinMessage(response?.data?.message || "Card number validated");
      setBinError("");
      setOtpValidateInput(cardNumber);
      if (response?.data?.require_email) {
        setRequireEmail(true);
      }
      // localStorage.setItem(CARD_NUMBER, cardNumber);
    }
  } catch (e) {
    setBinError(e?.data?.bin?.[0] || e?.data?.error || "Invalid card number");
    setBinMessage("");
  }
};

export const handleVerifyOTP = async ({
  data, resend = false, isLogin, setError, setPhoneError, setOtpValidate, setResendNumber, setMessage, setPhoneMessage,
  setLoading, countryCode, email, setOtpSent, setResendTimer
}) => {
  if (!data?.mobile_number || data.mobile_number.length < 7) {
    if (isLogin) {
      setError("Mobile Number must be at least 7 digits");
    } else {
      setPhoneError("Mobile Number must be at least 7 digits");
    }
    setLoading(false);
    return;
  }
  setLoading(true);
  try {
    const newData = {
      mobile_number: data.mobile_number,
      country_code: `+${countryCode}`,
      reason: isLogin ? "sign_in" : "sign_up",
    }

    if (email) {
      newData.email = email
    }

    const response = await sendOTP(newData);
    if (response.status === 200) {
      setOtpValidate(true);
      setResendNumber(true);
      if (isLogin) {
        setMessage(response?.data?.message || "OTP sent successfully");
        setError("");
      } else {
        setPhoneMessage(response?.data?.message || "OTP sent successfully");
        setPhoneError("");
      }
      if (resend) {
        setOtpSent(true);
        setResendTimer(90);
      }
    }
  } catch (e) {
    const errorMsg = e?.data?.error || e?.data?.mobile_number?.[0] || "Failed to send OTP";
    if (isLogin) {
      setError(errorMsg);
    } else {
      setPhoneError(errorMsg);
    }
  } finally {
    setLoading(false);
  }
};

export const handleResendOTP = async ({
  e, isLogin, setMessage, setError, setPhoneMessage, setPhoneError, setLoading, countryCode, email, setOtpSent, setResendTimer, 
  phoneNumber, setOtpValidate, setResendNumber
}) => {
  e.preventDefault();

  if (isLogin) {
    setMessage("");
    setError("");
  } else {
    setPhoneMessage("");
    setPhoneError("");
  }

  if (!phoneNumber || phoneNumber.length < 7) {
    const errorMsg = "Mobile Number must be at least 7 digits";
    if (isLogin) {
      setError(errorMsg);
    } else {
      setPhoneError(errorMsg);
    }
    setLoading(false);
    return;
  }

  setLoading(true);

  try {
    const data = {
      mobile_number: phoneNumber,
      country_code: `+${countryCode}`,
      type: "text",
    };
  
    if (email) {
      data.email = email
    }
    
    const response = await resendOTP(data);
    if (response.status === 200) {
      setOtpValidate(true);
      setResendNumber(true);
      setOtpSent(true);
      setResendTimer(90);
      if (isLogin) {
        setMessage(response?.data?.message || "OTP resent successfully");
        setError("");
      } else {
        setPhoneMessage(response?.data?.message || "OTP resent successfully");
        setPhoneError("");
      }
    }
  } catch (e) {
    const errorMsg = e?.data?.error || e?.data?.mobile_number?.[0] || "Failed to resend OTP";
    if (isLogin) {
      setError(errorMsg);
    } else {
      setPhoneError(errorMsg);
    }
  } finally {
    setLoading(false);
  }
};

export const handleTravelNavigation = ({ cardInfo, navigate }) => {
  const params = JSON.parse(cardInfo.param || '{}');

  if (cardInfo.flightDetail) {
    handleFlightNavigation({ params, navigate });
    return true;
  }

  if (cardInfo.hotelDetail) {
    handleHotelNavigation({ params, navigate });
    return true;
  }

  return false;
};

const handleFlightNavigation = ({ params, navigate }) => {
  const { data, page, filters } = params;

  if (page === SEARCH_PAGE) {
    const compressedParams = LZString?.compressToEncodedURIComponent(JSON.stringify(data));
    const filterParams = filters ? `&filters=${LZString?.compressToEncodedURIComponent(JSON.stringify(filters))}` : '';
    navigate(`/${TRAVEL_FLIGHT_MODULE}?page=${SEARCH_PAGE}&searchParam=${compressedParams}${filterParams}`);
    return;
  }

  if (page === DETAIL_PAGE) {
    navigate(`/${TRAVEL_FLIGHT_MODULE}?page=${DETAIL_PAGE}&step=${params?.step}&searchParam=${params?.searchParam}
      &flightData=${params?.flightData}&flightDetailData=${params?.flightDetailData}`);
    return;
  }
};

const handleHotelNavigation = ({ params, navigate }) => {
  const { hotelData, page, filters, hotelDetailData, searchParam } = params;
  const filterParams = filters ? `&filters=${LZString.compressToEncodedURIComponent(JSON.stringify(filters))}` : '';
  const hotelDetailDataEncoded = hotelDetailData ? LZString.compressToEncodedURIComponent(JSON.stringify(hotelDetailData)) : '';

  switch (page) {
    case SEARCH_PAGE:
      navigate(`/${TRAVEL_HOTEL_MODULE}?page=${SEARCH_PAGE}&searchParam=${hotelData || ''}`);
      break;
    case LISTING_PAGE:
      navigate(`/${TRAVEL_HOTEL_MODULE}?page=${LISTING_PAGE}&searchParam=${searchParam || ''}${filterParams}`);
      break;
    case DETAIL_PAGE:
      navigate(`/${TRAVEL_HOTEL_MODULE}?page=${DETAIL_PAGE}&searchParam=${searchParam || ''}${filterParams}
        &hotelDetailData=${hotelDetailDataEncoded || ''}`);
      break;
      default:
      navigate(HOMEPAGE);
  }
};

const navigateBasedOnCardInfo = async ({ cardInfo, navigate }) => {
  if (!cardInfo || Object.keys(cardInfo).length === 0) {
    return navigate(HOMEPAGE);
  }

  if (cardInfo.subscription) {
    return navigate(SUBSCRIPTION_LISTING);
  }

  if (cardInfo.flightDetail || cardInfo.hotelDetail) {
    const wasHandled = handleTravelNavigation({ cardInfo, navigate });
    if (wasHandled) return;
  }

  if (cardInfo.commerce) {
    const commerceUrl = cardInfo.slug ? `/${COMMERCE_MODULE}/${cardInfo.slug}` : COMMERCE_MODULE;
    return navigate(commerceUrl);
  }

  if (cardInfo.popularCategory) {
    return navigate(`/${REWARD_LISTING}/?filters={"brand_categories":[${cardInfo.id}]}`);
  }

  if (cardInfo.rewards || cardInfo.offer_type || cardInfo.fromOfferListing) {
    return await navigateToRewardsOrOffers({ cardInfo, navigate });
  }

  return navigate(HOMEPAGE);
};

const navigateToRewardsOrOffers = async ({ cardInfo, navigate, isLogin, setError, setPhoneError }) => {
  try {
    if (cardInfo.offer_type) {
      const path = cardTitleToPath(cardInfo.offer_type);
      const rewardPath = `/${REWARD_LISTING}/${path}/${cardInfo.id}/${cardInfo.slug}`;
      return navigate(rewardPath);
    }
    return navigate(HOMEPAGE);
  } catch {
    const errorMsg = "Failed to retrieve user details. Please try again.";
    if (isLogin) {
      setError(errorMsg);
    } else {
      setPhoneError(errorMsg);
    }
    return navigate(HOMEPAGE);
  }
};

export const cleanupState = ({ setWindowLocationUrl, setLoading, setCardInfo, setShowLoginForm, setShowLogin, setShowSignUp }) => {
  setWindowLocationUrl("");
  setLoading(false);
  closeAuthForm({ setShowLoginForm, setShowLogin, setShowSignUp });
  setCardInfo({});
};

export const cleanupAndNavigate = ({ 
  path, setWindowLocationUrl, setLoading, setCardInfo, navigate, setShowLoginForm, setShowLogin, setShowSignUp 
}) => {
  setWindowLocationUrl("");
  setLoading(false);
  closeAuthForm({ setShowLoginForm, setShowLogin, setShowSignUp });
  setCardInfo({});
  navigate(path);
};

export const handlePostAuthNavigation = async ({
  cardInfo, windowLocationUrl, navigate, setWindowLocationUrl, setLoading, setCardInfo, setShowLoginForm, setShowLogin, 
  setShowSignUp, actualUrl, isLogin, setError, setPhoneError, withoutBinSignUp
}) => {
  if (cardInfo?.flightDetail || cardInfo?.hotelDetail) {
    await navigateBasedOnCardInfo({ cardInfo, navigate });
    cleanupState({ setWindowLocationUrl, setLoading, setCardInfo, setShowLoginForm, setShowLogin, setShowSignUp });
    return;
  }

  if (cardInfo?.search) {
    cleanupAndNavigate({ path: windowLocationUrl, setWindowLocationUrl, setLoading, setCardInfo, navigate, setShowLoginForm, 
      setShowLogin, setShowSignUp });
    return;
  }

  if (windowLocationUrl) {
    await handleWindowLocationNavigation({ windowLocationUrl, navigate, cardInfo, actualUrl, isLogin, setError, setPhoneError, 
      withoutBinSignUp, setShowLoginForm, setShowLogin, setShowSignUp, setWindowLocationUrl, setLoading, setCardInfo });
    return;
  }

  await navigateBasedOnCardInfo({ cardInfo, navigate });
  closeAuthForm({ setShowLoginForm, setShowLogin, setShowSignUp });
};

export const handleWindowLocationNavigation = async ({
  windowLocationUrl, navigate, cardInfo, actualUrl, isLogin, setError, setPhoneError, withoutBinSignUp, setShowLoginForm, 
  setShowLogin, setShowSignUp, setWindowLocationUrl, setLoading, setCardInfo
}) => {
  const url = new URL(windowLocationUrl);
  const path = url.pathname + url.search;
  const isRewardListingPage = windowLocationUrl.includes(REWARD_LISTING);
  if( cardInfo?.fromOfferListing) {
    cleanupAndNavigate({ path, setWindowLocationUrl, setLoading, setCardInfo, navigate, setShowLoginForm, setShowLogin,
      setShowSignUp });
    await navigateBasedOnCardInfo({ cardInfo, navigate });
    return;
  }

  if (windowLocationUrl === actualUrl) {
    await navigateBasedOnCardInfo({ cardInfo, navigate });
    closeAuthForm({ setShowLoginForm, setShowLogin, setShowSignUp });
    return;
  }

  if (path.includes(`/${REWARD_LISTING}/`) || isRewardListingPage) {
    try {
      closeAuthForm({ setShowLoginForm, setShowLogin, setShowSignUp });
      navigate(path);
    } catch {
      const errorMsg = "Failed to retrieve user details. Please try again.";
      if (isLogin) {
        setError(errorMsg);
      } else {
        setPhoneError(errorMsg);
      }
      cleanupAndNavigate({ path: HOMEPAGE, setWindowLocationUrl, setLoading, setCardInfo, navigate, setShowLoginForm, setShowLogin,
        setShowSignUp });
    }
    return;
  }

  if (withoutBinSignUp && isRewardListingPage) {
    cleanupAndNavigate({ path, setWindowLocationUrl, setLoading, setCardInfo, navigate, setShowLoginForm, setShowLogin, 
      setShowSignUp });
    return;
  }
  
  cleanupAndNavigate({ path: HOMEPAGE, setWindowLocationUrl, setLoading, setCardInfo, navigate, setShowLoginForm, setShowLogin,
  setShowSignUp });
};

// Separate functions for passkey flow
export const handlePasskeyCheck = (loginData) => {
  setLocalStorageItem('isPasskeyPresent', !!loginData?.is_passkey_present);
  return !!loginData?.is_passkey_present;
};

export const handlePasskeySignInInit = async () => {
  try {
    const response = await passkeySignInInitAPI();
    if (response.status !== 200 || !response.data) {
      throw new Error('Failed to initialize passkey sign-in');
    }
    
    // Parse if wrapped in { options: string }
    let options = response.data;
    if (options?.options && typeof options.options === 'string') {
      try {
        options = JSON.parse(options.options);
      } catch (parseError) {
        throw new Error('Failed to parse passkey options: ' + parseError.message);
      }
    }

    // Validate essential fields for authentication (not registration)
    if (!options.challenge || !options.allowCredentials || options.allowCredentials.length === 0) {
      throw new Error('Invalid authentication options: Missing challenge or allowCredentials');
    }

    // CRITICAL FIX: Extract hostname from rpId if it's a full URL
    if (options.rpId) {
      const cleanRpId = extractHostname(options.rpId);
      options.rpId = cleanRpId;
    }

    return options;
  } catch (error) {
    console.error('Passkey sign-in init error:', error);
    throw error;
  }
};

// Main function to handle passkey verification (authentication only)
export const handlePasskeyVerification = async (initOptions) => {
  try {
    
    // Ensure secure context and API support
    if (!window.isSecureContext || !window.PublicKeyCredential) {
      throw new Error('Secure context or WebAuthn not supported');
    }

    // Validate options for authentication only
    if (!initOptions.challenge || !initOptions.allowCredentials || initOptions.allowCredentials.length === 0) {
      throw new Error('Invalid options: Authentication requires challenge and allowCredentials');
    }

    // CRITICAL: Ensure rpId is hostname only (double-check)
    let effectiveRpId = initOptions?.rpId || window.location.hostname;
    if (effectiveRpId.includes('://')) {
      effectiveRpId = extractHostname(effectiveRpId);
    }

    // Prepare options for navigator.credentials.get() (AUTHENTICATION ONLY)
    let publicKeyOptions = {
      challenge: base64UrlToArrayBuffer(initOptions.challenge),
      allowCredentials: initOptions.allowCredentials.map(cred => ({
        id: base64UrlToArrayBuffer(cred.id),
        type: cred.type || 'public-key',
        transports: cred.transports || ['internal', 'hybrid']
      })),
      rpId: effectiveRpId,
      timeout: initOptions.timeout || 60000,
      userVerification: initOptions.userVerification || 'required'
    };
    
    const assertion = await navigator.credentials.get({
      publicKey: publicKeyOptions,
      mediation: "required"
    });

    if (!assertion) {
      throw new Error('No assertion received from authenticator - passkey validation failed');
    }

    // Verify it's an authentication response (not registration)
    if (assertion.type !== 'public-key' || !assertion.response || !assertion.response.signature) {
      throw new Error('Invalid assertion: Not a valid authentication response');
    }

    // Prepare assertion data for backend (base64url)
    const assertionData = {
      credential : {
        id: bufferToBase64Url(assertion.rawId),
        rawId: bufferToBase64Url(assertion.rawId),
        type: assertion.type,
        response: {
          authenticatorData: bufferToBase64Url(assertion.response.authenticatorData),
          clientDataJSON: bufferToBase64Url(assertion.response.clientDataJSON),
          signature: bufferToBase64Url(assertion.response.signature),
          userHandle: assertion.response.userHandle ? bufferToBase64Url(assertion.response.userHandle) : null
        }
      }
    };

    return assertionData;
  } catch (error) {
    if (error.name === 'NotAllowedError') {
      throw new Error('Passkey authentication cancelled by user or device');
    } else if (error.name === 'SecurityError') {
      throw new Error('Security error: Check rpId match or secure context');
    } else if (error.name === 'InvalidStateError') {
      throw new Error('Invalid state: No matching credentials found');
    } else if (error.message.includes('rpId mismatch')) {
      throw new Error('Domain mismatch: Passkey registered on different site');
    }
    throw new Error(`Passkey validation failed: ${error.message}`);
  }
};

export const handlePasskeySignIn = async (assertionData) => {
  try {
    const response = await passkeySignInAPI(assertionData);
    
    if (response.status !== 200 || !response.data) {
      throw new Error('Failed to complete passkey sign-in');
    }
    
    sessionStorage.setItem('passkeyToken', JSON.stringify(response?.data?.token));
    return response.data;
  } catch (error) {
    console.error('Passkey sign-in error:', error);
    throw error;
  }
};

export const handleVerifyLogin = async ({
  data, setLoading, setError, countryCode, email, login, setMessage, setIsAuthenticated, updateCreditCard, setSubVarientIdGlobal, 
  setSelectedCardData, setUserData, setCardInfo, cardInfo, windowLocationUrl, navigate, setWindowLocationUrl, setShowLoginForm, 
  setShowLogin, setShowSignUp, actualUrl, isLogin, setPhoneError, withoutBinSignUp, setAlert, setShowPasskeyModal, isAuthPageMode, 
  setShowNameModal, passkeyPopupshow, setShowWhatsAppOptIn, setOtp = () => {}
}) => {
  setLoading(true);
  setError("");

  try {
    const newData = {
      mobile_number: data.mobile_number,
      country_code: `+${countryCode}`,
      otp: data.otp,
    };

    if (email) {
      newData.email = email
    }
    const response = await loginAPI(newData);
    if (response.status !== 200) {
      setOtp("");
      setError("Login failed. Please try again.");
      return;
    }
    clearLocalStorage();
    localStorage.setItem('token', response?.data?.token);
    setShowWhatsAppOptIn(response?.data?.whatsapp_opt_in || false);
    setLocalStorageItem('showWhatsAppOptIn', response?.data?.whatsapp_opt_in ? 'true' : 'false');
    // Save isPasskeyPresent flag early
    const hasPasskey = handlePasskeyCheck(response?.data);
    localStorage.setItem('isPasskeyPresent', hasPasskey);
    
    // Ensure token is persisted before starting passkey flow
    const waitForToken = async (timeout = 1000) => {
      const keys = ['token', 'authToken', 'accessToken', 'access_token'];
      const interval = 50;
      const max = Math.ceil(timeout / interval);
      for (let i = 0; i < max; i++) {
        if (keys.some(k => !!localStorage.getItem(k))) return true;
      await new Promise(r => setTimeout(r, interval));
      }
      return false;
    };

    // wait up to 1s for login(token) to persist token in localStorage, then run passkey flow
    await waitForToken(1000);

    if (hasPasskey) {
      try {
        const init = await handlePasskeySignInInit();
        const assertion = await handlePasskeyVerification(init);
        const result = await handlePasskeySignIn(assertion);

        // Success → use passkey token
        sessionStorage.setItem("passkeyToken", result.token);
        login(result?.data?.token);
      } catch (err) {
        setAlert({
          show: true,
          message: "Biometric required. Authentication failed.",
          type: "error",
        });
        setLoading(false);
        sessionStorage.removeItem("passkeyToken");
      }
    }

    login(response?.data?.token);
    setMessage(response?.data?.message || "Login successful");
    setIsAuthenticated(true);
    updateCreditCard(response.data.cards);
    const defaultCard = response.data.cards.find(card => card.is_default === true) || response.data.cards[0];
    const { selectedCard, selectedCardId } = getPreferredCardSelection(response.data.cards, defaultCard);
    if (selectedCardId !== null) {
      localStorage.setItem(SUBID, JSON.stringify(selectedCardId));
      setSubVarientIdGlobal(selectedCardId);
    }
    if (selectedCard && Object.keys(selectedCard).length > 0) {
      localStorage.setItem(SELECTEDCARD, JSON.stringify(selectedCard));
      setSelectedCardData(selectedCard);
    }
    const editedUserData = getEditNameStatus(response?.data?.user);
    setUserData(editedUserData);
    localStorage.setItem(USERDATA, JSON.stringify(editedUserData));
    trackAuthSuccess({
      action: "sign_in",
      method: "otp",
      message: response?.data?.message || "User Signed In Successfully!",
      user: response?.data?.user,
      pageType: "auth",
    });
    removeLocalStorageItem(CARD_NUMBER);

    if(editedUserData?.canEditUserName && response?.data?.nudge) {
      setShowNameModal(true);
    }
    // show passkey modal after login if user doesn't have passkey
    if (!hasPasskey && passkeyPopupshow) {
      setTimeout(() => {
        setShowPasskeyModal(response?.data?.nudge);
      }, 500);
    }

    if (isAuthPageMode) {
      cleanupAndNavigate({ 
        path: `/${REWARD_LISTING}`, setWindowLocationUrl, setLoading, setCardInfo, navigate, setShowLoginForm, setShowLogin, 
        setShowSignUp 
      });
      return navigate(`/${REWARD_LISTING}`); 
    }

    setTimeout(async () => {
      await handlePostAuthNavigation({ cardInfo, windowLocationUrl, navigate, setWindowLocationUrl, setLoading, setCardInfo,
        setShowLoginForm, setShowLogin, setShowSignUp, actualUrl, isLogin, setError, setPhoneError, withoutBinSignUp });
    }, 100);
  } catch (err) {
    setOtp("");
    setError(err?.data?.error || "An error occurred during login. Please try again.");
    setAlert({
      show: true,
      message: err?.data?.error || "An error occurred during login. Please try again.",
      type: "error",
    });
  } finally {
    setLoading(false);
  }
};

export const handleVerifySignUp = async ({
  data, otpValidateInput, setBinError, setPhoneError, setPhoneMessage, setLoading, countryCode, email, login, setIsAuthenticated,
  updateCreditCard,  setSubVarientIdGlobal, setSelectedCardData, setUserData, setBinMessage, setShowWhatsAppOptIn, setAlert,
  cardInfo, windowLocationUrl, navigate, setWindowLocationUrl, setShowLoginForm, setShowLogin, setShowSignUp, actualUrl, isLogin,
  setError, setCardInfo, withoutBinSignUp, setShowPasskeyModal, isAuthPageMode, supportsUidSignUp, requireUid, userId,
  setShowNameModal, passkeyPopupshow, setOtp = () => {}
}) => {
  const trimmedUserId = userId?.trim() || "";
  const shouldUseUidSignUp = supportsUidSignUp && Boolean(trimmedUserId);
  const cardNumToUse = localStorage.getItem("card_number") || otpValidateInput;
  if (requireUid && !trimmedUserId) {
    setPhoneError("UID is required");
    setLoading(false);
    return;
  }

  if (!shouldUseUidSignUp && (!cardNumToUse || cardNumToUse.length !== 8)) {
    setBinError("Please enter a valid 8-digit card number");
    setLoading(false);
    return;
  }

  setLoading(true);
  try {
    const buildSignUpData = () => {
      const baseData = {
        mobile_number: data.mobile_number,
        country_code: `+${countryCode}`,
        otp: data.otp,
      };

      if (email) {
        baseData.email = email;
      }

      if (data.name) {
        baseData.name = data.name;
      }

      return baseData;
    };

    const baseData = buildSignUpData();
    const signUpData = { bin: cardNumToUse, ...baseData };
    const uidSignUpData = { uid: trimmedUserId, ...baseData };

    const response = await (shouldUseUidSignUp ? UidSignUpAPI(uidSignUpData) : signUpAPI(signUpData));

    if (response.status === 200) {
      setPhoneMessage(response?.data?.message || "Signup successfully");
      const token = response?.data?.token;
      login(token);
      setIsAuthenticated(true);
      setShowWhatsAppOptIn(response?.data?.whatsapp_opt_in || false);
      setLocalStorageItem('showWhatsAppOptIn', response?.data?.whatsapp_opt_in ? 'true' : 'false');
      updateCreditCard(response.data.cards);
      const defaultCard = response.data.cards.find(card => card.is_default === true) || response.data.cards[0];
      const { selectedCard, selectedCardId } = getPreferredCardSelection(response.data.cards, defaultCard);

      if (selectedCardId !== null) {
        localStorage.setItem('subId', JSON.stringify(selectedCardId));
        setSubVarientIdGlobal(selectedCardId);
      }
      if (selectedCard && Object.keys(selectedCard).length > 0) {
        localStorage.setItem('selectedCard', JSON.stringify(selectedCard));
        setSelectedCardData(selectedCard);
      }
      const editedUserData = getEditNameStatus(response?.data?.user);
      setUserData(editedUserData);
      localStorage.setItem(USERDATA, JSON.stringify(editedUserData));
      trackAuthSuccess({
        action: "sign_up",
        method: shouldUseUidSignUp ? "uid_otp" : "otp",
        message: response?.data?.message || "Signup successfully",
        user: response?.data?.user,
        pageType: "auth",
      });
      // show passkey modal after signup
      if(passkeyPopupshow) {
        setShowPasskeyModal(response?.data?.passkey_nudge);
      }
      removeLocalStorageItem(CARD_NUMBER);

      if (isAuthPageMode) {
        if (shouldUseUidSignUp) {
          removeLocalStorageItem(UID);
        }
        cleanupAndNavigate({ 
          path: `/${REWARD_LISTING}`, setWindowLocationUrl, setLoading, setCardInfo, navigate, setShowLoginForm, setShowLogin, 
          setShowSignUp 
        });

        if(editedUserData?.canEditUserName && response?.data?.nudge) {
          setShowNameModal(true);
          // setShowPasskeyModal(response?.data?.nudge);
        }
        return navigate(`/${REWARD_LISTING}`);
      }

      setTimeout(async () => {
        await handlePostAuthNavigation({ cardInfo, windowLocationUrl, navigate, setWindowLocationUrl, setLoading, setCardInfo,
          setShowLoginForm, setShowLogin, setShowSignUp, actualUrl, isLogin, setError, setPhoneError, withoutBinSignUp });
      }, 100);
    } else {
      setOtp("");
      setPhoneError("Signup failed. Please try again.");
      setAlert({
        show: true,
        message: "Signup failed. Please try again.",
        type: "error",
      });
    }
  } catch (e) {
    setOtp("");
    console.error("Signup error:", e);
    if (e?.data?.bin?.[0]) {
      setBinMessage("");
      setBinError(e?.data?.bin?.[0] || "Invalid card number");
      setAlert({
        show: true,
        message: e?.data?.bin?.[0] || "Invalid card number",
        type: "error",
      });
    } else {
      setPhoneMessage("");
      setPhoneError(e?.data?.mobile_number?.[0] || e?.data?.error || "An error occurred during signup");
      setAlert({
        show: true,
        message: e?.data?.mobile_number?.[0] || e?.data?.error || "An error occurred during signup",
        type: "error",
      });
    }
  } finally {
    setLoading(false);
  }
};

export const onSubmit = async ({
  e, isSignUp, name, phoneNumber, email, otp, isLogin, otpValidate, setFormErrors, resendNumber, setLoading, setMessage, setError,
  setPhoneMessage, setPhoneError, setIsAuthenticated, login, countryCode, setSubVarientIdGlobal, setSelectedCardData,
  updateCreditCard, setUserData, closeAuthForm, cardInfo, navigate, actualUrl, setShowWhatsAppOptIn, 
  setWindowLocationUrl, windowLocationUrl, setCardInfo, otpValidateInput, setBinError, setBinMessage, setOtpSent,
  setResendTimer, setOtpValidate, setResendNumber, setShowLoginForm, setShowLogin, setShowSignUp, withoutBinSignUp, 
  setOtpValidateInput, setAlert, setShowPasskeyModal, isAuthPageMode, supportsUidSignUp, requireUid, requireName, userId,
  setShowNameModal, passkeyPopupshow, setOtp = () => {}
}) => {
  e.preventDefault();
  const data = {
    name: isSignUp ? name : "",
    mobile_number: phoneNumber,
    email: email,
    otp: otp
  };
  const validationResult = isLogin
    ? validateLoginForm(data, otpValidate)
    : validateSignUpForm(data, otpValidate, {
      requireName,
      requireUid,
      uid: userId,
    });
  if (!validationResult.isValid) {
    setFormErrors(validationResult.errors);
    setLoading(false);
    return;
  }

  setFormErrors({});

  if (!otpValidate || !resendNumber) {
    await handleVerifyOTP({
      data, resend: false, isLogin, setError, setPhoneError, setOtpValidate, setResendNumber, setMessage, setPhoneMessage,
      setLoading, countryCode, email, setOtpSent, setResendTimer, setAlert, userId
    });
  } else {
    if (isLogin) {
      await handleVerifyLogin({
        data, setLoading, setError, countryCode, email, login, setMessage, setIsAuthenticated, updateCreditCard, 
        setSubVarientIdGlobal, setSelectedCardData, setUserData, setCardInfo, cardInfo, windowLocationUrl, 
        navigate, setWindowLocationUrl, setShowLoginForm, setShowLogin, setShowSignUp, actualUrl, isLogin, setPhoneError, 
        withoutBinSignUp, setAlert, setShowPasskeyModal, isAuthPageMode, setShowNameModal, passkeyPopupshow,
        setShowWhatsAppOptIn, setOtp
      });
    } else {
      await handleVerifySignUp({
        data, otpValidateInput, setBinError, setPhoneError, setPhoneMessage, setLoading, countryCode, email, login, 
        setIsAuthenticated, updateCreditCard,  setSubVarientIdGlobal, setSelectedCardData, setShowWhatsAppOptIn, 
        setUserData, setBinMessage, cardInfo, windowLocationUrl, navigate, setWindowLocationUrl, setShowLoginForm, setShowLogin, 
        setShowSignUp, actualUrl, isLogin, setError, setCardInfo, withoutBinSignUp, setAlert, setShowPasskeyModal,
        isAuthPageMode, supportsUidSignUp, requireUid, userId, setShowNameModal, passkeyPopupshow, setOtp
      });
    }
  }
};

export const handleChangeNumber = ({ 
  setResendNumber, setOtpValidate, isLogin, setMessage, setError, setPhoneMessage, setPhoneError, setOtp = () => {}
}) => {
  setResendNumber(false);
  setOtpValidate(false);
  setOtp("");

  if (isLogin) {
    setMessage("");
    setError("");
  } else {
    setPhoneMessage("");
    setPhoneError("");
  }
};

export const handleEnterKey = ({
  e, isSignUp, name, phoneNumber, email, otp, isLogin, otpValidate, setFormErrors, resendNumber, setLoading, setMessage, setError,
  setPhoneMessage, setPhoneError, setIsAuthenticated, login, countryCode, setSubVarientIdGlobal, setSelectedCardData, 
   updateCreditCard, setUserData, closeAuthForm, cardInfo, navigate, actualUrl, 
  setWindowLocationUrl, windowLocationUrl, setCardInfo, otpValidateInput, setBinError, setBinMessage, setOtpSent, setResendTimer, 
  setOtpValidate, setResendNumber, setShowLoginForm, setShowLogin, setShowSignUp, withoutBinSignUp, setOtpValidateInput, setAlert, 
  setShowPasskeyModal, isAuthPageMode, supportsUidSignUp, requireUid, requireName, userId, setShowNameModal,
  passkeyPopupshow, setShowWhatsAppOptIn, setOtp = () => {}
}) => {
  if (e.key === 'Enter') {
    onSubmit({
      e, isSignUp, name, phoneNumber, email, otp, isLogin, otpValidate, setFormErrors, resendNumber, setLoading, setMessage, 
      setError, setPhoneMessage, setPhoneError, setIsAuthenticated, login, countryCode, setSubVarientIdGlobal, setSelectedCardData, 
       updateCreditCard, setUserData, closeAuthForm, cardInfo, navigate, actualUrl, setShowSignUp,
      setWindowLocationUrl, windowLocationUrl, setCardInfo, otpValidateInput, setBinError, setBinMessage, setOtpSent, 
      setResendTimer, setOtpValidate, setResendNumber, setShowLoginForm, setShowLogin, withoutBinSignUp, setOtpValidateInput, 
      setAlert, setShowPasskeyModal, isAuthPageMode, supportsUidSignUp, requireUid, requireName, userId,
      setShowNameModal, passkeyPopupshow, setShowWhatsAppOptIn, setOtp
    });
  }
};
